import { COMPONENT_PLACEMENT_FRONT_AND_TOP, COMPONENT_PLACEMENT_TOP_ONLY } from '../../../component-classes/constants';

import type { PanelItem } from '../../panel-items/panel-item.class';
import type { PanelItemGroup } from '../../panel-item-groups/panel-item-group.class';

export type GroupCountertopEdgeSide = 'left' | 'right';

export type GetGroupCountertopEdgeParams = {
	group: PanelItemGroup;
	side: GroupCountertopEdgeSide;
};

/** True when the item carves a cutout into the countertop rather than only the panel face. */
export const isCountertopCutoutItem = (panelItem: PanelItem): boolean => {
	const { placement } = panelItem.item;
	return placement === COMPONENT_PLACEMENT_FRONT_AND_TOP || placement === COMPONENT_PLACEMENT_TOP_ONLY;
};

/**
 * Width of the countertop cutout a group carves into the top. Ported verbatim from the
 * Align Left / Align Right context-menu items, including their last-top-item-wins reduce,
 * so the automatic placement lands exactly where the manual snap would.
 * @param group the panel item group to measure
 * @returns cutout width in inches, 0 when nothing in the group reaches the countertop
 */
export const getGroupCountertopCutoutWidth = (group: PanelItemGroup): number => {
	return group.items.children.reduce(
		(acc, panelItem) => (isCountertopCutoutItem(panelItem as PanelItem) ? panelItem.item.cutout.width : acc)
		, 0
	);
};

/**
 * One side of a group's countertop cutout, in the panel-relative space `Breakpoint.center` uses.
 * @param params the group and which side to measure
 * @returns the edge position, or null when the group cuts nothing into the countertop
 */
export const getGroupCountertopEdge = (params: GetGroupCountertopEdgeParams): number | null => {
	const { group, side } = params;
	const cutoutWidth = getGroupCountertopCutoutWidth(group);
	if (!cutoutWidth) return null;

	const halfCutoutWidth = cutoutWidth / 2;
	return side === 'left' ? group.center.x - halfCutoutWidth : group.center.x + halfCutoutWidth;
};
